import { Link } from "react-router-dom";
import type { CatalogBrandSummary } from "@/lib/api-client";

export interface BrandQuickLinksProps {
  brands: CatalogBrandSummary[];
}

/**
 * Marques à la une en tuiles rectangle arrondi — même gabarit que
 * `CategoryQuickLinks.tsx` ("Acheter par rayon"), coins arrondis au lieu de
 * cercle. L'image remplit toute la tuile (`object-cover`, sans marge intérieure) :
 * les marges autour de l'image laissaient un cadre vide peu esthétique.
 * Distinct de `BrandsStrip.tsx` (liste de pastilles plus petites, plus bas sur
 * la page) : les deux coexistent, celui-ci occupe la position juste après
 * "Nouveautés" et juste avant "Acheter par rayon".
 */
const TILE_TINTS = ["#14213D", "#B8964A", "#24365E", "#8A7346", "#1B2A4A", "#3A4A6B", "#6B5B3A", "#C9A227"];

export function BrandQuickLinks({ brands }: BrandQuickLinksProps) {
  const featured = brands.filter((brand) => brand.isFeatured);
  if (featured.length === 0) return null;

  return (
    <section className="container-vvs py-8 lg:py-10">
      <h2 className="font-display text-xl font-semibold text-navy">Marques à la une</h2>
      <ul className="mt-5 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 lg:grid lg:grid-cols-4 lg:gap-6 lg:overflow-visible">
        {featured.map((brand, index) => (
          <li key={brand.id} className="w-28 flex-shrink-0 snap-start lg:w-auto">
            <Link to={`/marques/${brand.slug}`} className="group flex flex-col items-center gap-2.5 text-center">
              <span className="block overflow-hidden rounded-2xl border border-line bg-cream2 shadow-sm transition-transform duration-200 group-hover:scale-105 group-hover:border-gold">
                {brand.logoUrl ? (
                  <img
                    src={brand.logoUrl}
                    alt=""
                    loading="lazy"
                    className="block h-24 w-24 object-cover sm:h-28 sm:w-28"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    style={{ backgroundColor: TILE_TINTS[index % TILE_TINTS.length] }}
                    className="flex h-24 w-24 items-center justify-center font-display text-2xl font-semibold text-cream sm:h-28 sm:w-28"
                  >
                    {brand.name.charAt(0)}
                  </span>
                )}
              </span>
              <span className="font-body text-[11px] font-semibold uppercase tracking-wide text-ink">
                {brand.name}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
